import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { useGallery } from "@/contexts/GalleryContext";
import { titleToDetailKey, activityDetails } from "@/data/portfolio-details";
import { useNavigate } from "react-router-dom";

interface ListItemProps {
  icon?: string;
  emoji?: string;
  title: string;
  subtitle: string;
  link?: string;
  linkLabel?: string;
  delay?: number;
  noBorder?: boolean;
}

const ListItem = ({ icon, emoji, title, subtitle, link, linkLabel = "Visit", delay = 0, noBorder = false }: ListItemProps) => {
  const { addItem, hasItem } = useGallery();
  const navigate = useNavigate();
  const [justAdded, setJustAdded] = useState(false);
  const detailKey = titleToDetailKey[title];
  const isInGallery = detailKey ? hasItem(detailKey) : false;

  const handleView = () => {
    // External link mode — no gallery logic
    if (link && !detailKey) {
      window.open(link, "_blank", "noopener,noreferrer");
      return;
    }
    if (!detailKey) return;
    const detail = activityDetails[detailKey];
    if (!detail) return;
    if (isInGallery) {
      navigate("/gallery");
      return;
    }
    addItem({ ...detail, addedAt: Date.now() });
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 2200);
  };

  return (
    <div
      className="flex items-center gap-3.5 rounded-xl px-2 py-1.5 anim-fade-in-up-sm"
      style={{ animationDelay: `${delay}s` }}
    >
      <button onClick={handleView} className="shrink-0 cursor-pointer">
        {icon ? (
          <img src={icon} alt={title} loading="eager" decoding="sync" className="h-11 w-11 sm:h-14 sm:w-14 rounded-xl object-contain drop-shadow-[0_0.5px_1.5px_rgba(0,0,0,0.07)]" />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-lg">
            {emoji ? emoji : title.charAt(0)}
          </div>
        )}
      </button>
      <button onClick={handleView} className="flex-1 min-w-0 text-left cursor-pointer">
        <p className="text-[12px] sm:text-[15px] font-bold text-foreground truncate">{title}</p>
        <p className="text-[11px] sm:text-sm text-muted-foreground truncate">{subtitle}</p>
      </button>
      <button
        onClick={handleView}
        className={`shrink-0 rounded-full border px-3 py-1 text-[10px] sm:px-4 sm:py-1.5 sm:text-xs font-medium transition-all hover:scale-105 hover:shadow-sm ${
          justAdded
            ? "border-green-800/70 bg-green-800/15 text-green-900"
            : isInGallery
            ? "border-primary/30 bg-primary/5 text-primary hover:bg-primary/10"
            : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
        }`}
      >
        <AnimatePresence mode="wait">
          {justAdded ? (
            <motion.span
              key="added"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="flex items-center gap-1"
            >
              <Check className="h-3 w-3" /> Added
            </motion.span>
          ) : isInGallery ? (
            <motion.span key="gallery" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              Gallery ↗
            </motion.span>
          ) : (
            <motion.span key="view" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {link && !detailKey ? linkLabel : "View"}
            </motion.span>
          )}
        </AnimatePresence>
      </button>
    </div>
  );
};

export default ListItem;
